import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Modal, Checkbox, Switch, Tooltip } from 'antd';
import {
    Button,
    ColumnProps,
    FormDatePicker,
    FormInput,
    FormSelect,
    FormSubmitButton,
    FormTextArea,
    MasterDetailForm,
    Row,
    Table,
    notification,
} from '@digital/ui';
import { ISasDocumentoOrdemCompra, getDocumentosOrdemCompraAjusteOrcamentario, updateSasDocumentoOrdemCompraCommand } from '@app/api/sas/sasDocumentoOrdemCompra';
import { enviarAjusteOrcamentario } from '@app/api/sas/ordemCompra';
import { Demanda, getDemanda, TipoDemanda } from '@app/api/demanda';
import { ordemCompraIdState, reloadDadosGeraisState, dadosGeraisState } from '../atoms';
import { thousandSeparator } from '@app/helpers/stringHelper';
import moment from 'moment';
import * as Yup from 'yup';
import CurrencyInput from '@app/components/CurrencyInput';

const infoAtualStyle: React.CSSProperties = {
    color: '#8c8c8c',
    fontSize: 13,
};

const CRITERIO_AJUSTE_VALOR_OPTIONS = [
    { text: 'Melhor proposta negociada', value: 1 },
    { text: 'Melhor proposta equalizada', value: 2 },
    { text: 'Baseline ACS', value: 3 },
];

interface Props {
    open: boolean;
    onClose: () => void;
}

const ModalDocumentacaoRequisitante = ({ open, onClose }: Props) => {
    const [reloadDadosGerais, setReloadDadosGerais] = useRecoilState(reloadDadosGeraisState);
    const [reloadDocumentos, setReloadDocumentos] = useState(false);
    const [documentos, setDocumentos] = useState<ISasDocumentoOrdemCompra[]>([]);
    const ordemCompraId = useRecoilValue(ordemCompraIdState);
    const dadosGerais = useRecoilValue(dadosGeraisState);

    const [demanda, setDemanda] = useState<Demanda | null>(null);
    const [isAjusteValor, setIsAjusteValor] = useState(false);
    const [isAjustePrazo, setIsAjustePrazo] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const ehAditivo = dadosGerais.tipo === TipoDemanda.Aditivo;
    const ehNovaContratacao = dadosGerais.tipo === TipoDemanda['Nova Contratação'];

    useEffect(() => {
        if (open) {
            getDocumentosOrdemCompraAjusteOrcamentario(ordemCompraId).then(data => {
                if (data) setDocumentos(data);
            });
            if (dadosGerais.demandaId) {
                getDemanda(dadosGerais.demandaId).then(data => {
                    if (data) setDemanda(data);
                });
            }
        }
    }, [open, reloadDocumentos]);

    const handleCancel = () => {
        setIsAjusteValor(false);
        setIsAjustePrazo(false);
        setFormKey(k => k + 1);
        onClose();
    };

    const handleSubmit = (values: any) => {
        enviarAjusteOrcamentario({
            ordemCompraId,
            isAjusteManualValor: isAjusteValor,
            valorAjusteManual: isAjusteValor ? values.valorAjusteManual : undefined,
            criterioAjusteValor: isAjusteValor ? values.criterioAjusteValor : undefined,
            isAjusteManualPrazo: isAjustePrazo,
            novoPrazo: isAjustePrazo && ehAditivo ? values.novoPrazo : undefined,
            novoPrazoMeses: isAjustePrazo && ehNovaContratacao ? values.novoPrazoMeses : undefined,
            justificativa: values.justificativa || undefined,
        }).then((data: any) => {
            if (data && data.valid) {
                notification.success('Reabertura da RC enviada para o SGC.');
                setReloadDadosGerais(!reloadDadosGerais);
            }
        });
        handleCancel();
    };

    const onChangeEditavelRequisitante = (documentoId: string, editavelRequisitante: boolean) => {
        updateSasDocumentoOrdemCompraCommand({
            sasDocumentoOrdemCompraId: documentoId,
            editavelRequisitante,
        }).then(() => {
            setReloadDocumentos(!reloadDocumentos);
        });
    };

    const validationSchema = Yup.object().shape({
        valorAjusteManual: isAjusteValor
            ? Yup.number()
                .typeError('Informe um valor válido.')
                .min(0.01, 'O valor deve ser maior que zero.')
                .required('O novo valor OC mínimo é obrigatório.')
            : Yup.number().nullable(),
        criterioAjusteValor: isAjusteValor
            ? Yup.number().required('O critério para ajuste de valor é obrigatório.')
            : Yup.number().nullable(),
        novoPrazo: isAjustePrazo && ehAditivo
            ? Yup.string().required('A nova data de término é obrigatória.')
            : Yup.string().nullable(),
        novoPrazoMeses: isAjustePrazo && ehNovaContratacao
            ? Yup.number()
                .typeError('Informe um valor válido.')
                .min(1, 'Mínimo de 1 mês.')
                .required('Os meses a adicionar são obrigatórios.')
            : Yup.number().nullable(),
        justificativa: Yup.string().required('A justificativa é obrigatória.'),
    });

    const columns = (): ColumnProps<any>[] => [
        { title: 'Descrição', dataIndex: 'descricao' },
        { title: 'Arquivo', dataIndex: 'arquivoNomeDownload' },
        {
            title: 'Editável para o Requisitante?',
            className: 'td-centralizado',
            render: (_: number, record: any) => (
                <Checkbox
                    checked={record.editavelRequisitante}
                    onChange={(e) => onChangeEditavelRequisitante(record.id, e.target.checked)}
                />
            ),
        },
    ];

    return (
        <Modal
            title="Documentação a ser enviada ao Requisitante"
            width="980px"
            visible={open}
            onCancel={handleCancel}
            footer={null}
        >
            <MasterDetailForm
                key={`formAjusteOrcamentario_${formKey}`}
                initialValues={{
                    valorAjusteManual: undefined,
                    criterioAjusteValor: undefined,
                    novoPrazo: undefined,
                    novoPrazoMeses: undefined,
                    justificativa: '',
                }}
                validationSchema={validationSchema}
                actions={[
                    <Button type="link" onClick={handleCancel}>Voltar</Button>,
                    <FormSubmitButton
                        title="Confirmar"
                        type="primary"
                        noIcon
                        onClick={(values: any) => handleSubmit(values)}
                    />,
                ]}
            >
                {/* Seção: Ajuste de Valor */}
                <Row>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', marginBottom: 12 }}>
                        <span style={{ fontWeight: 500 }}>Ajustar valor?</span>
                        <Switch
                            checked={isAjusteValor}
                            onChange={(checked) => setIsAjusteValor(checked)}
                        />
                        {demanda && demanda.expectativaValorFinal != null && (
                            <span style={infoAtualStyle}>
                                Valor OC atual:{' '}
                                <strong>R$ {thousandSeparator(demanda.expectativaValorFinal)}</strong>
                            </span>
                        )}
                    </div>
                    {isAjusteValor && (
                        <CurrencyInput
                            name="valorAjusteManual"
                            label="Novo valor OC mínimo (R$)"
                            span={4}
                        />
                    )}
                    {isAjusteValor && (
                        <FormSelect
                            label="Critério para ajuste de valor"
                            name="criterioAjusteValor"
                            options={CRITERIO_AJUSTE_VALOR_OPTIONS}
                            span={4}
                        />
                    )}
                </Row>

                {/* Seção: Ajuste de Prazo */}
                <Row>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', marginBottom: 12 }}>
                        <span style={{ fontWeight: 500 }}>Ajustar prazo?</span>
                        <Switch
                            checked={isAjustePrazo}
                            onChange={(checked) => setIsAjustePrazo(checked)}
                        />
                        {ehNovaContratacao && demanda && demanda.expectativaDePrazo != null && (
                            <span style={infoAtualStyle}>
                                Prazo acumulado atual:{' '}
                                <strong>{demanda.expectativaDePrazo} meses</strong>
                            </span>
                        )}
                        {ehAditivo && demanda && demanda.novoPrazo && (
                            <span style={infoAtualStyle}>
                                Prazo aditivo atual:{' '}
                                <strong>{moment(demanda.novoPrazo).format('DD/MM/YYYY')}</strong>
                            </span>
                        )}
                    </div>
                    {isAjustePrazo && ehNovaContratacao && (
                        <Tooltip title="O valor informado em meses será somado ao prazo acumulado atual">
                            <FormInput
                                label="Meses a adicionar"
                                name="novoPrazoMeses"
                                type="number"
                                span={4}
                            />
                        </Tooltip>
                    )}
                    {isAjustePrazo && ehAditivo && (
                        <FormDatePicker
                            label="Nova data de término"
                            name="novoPrazo"
                            format="DD/MM/YYYY"
                            span={4}
                        />
                    )}
                </Row>

                {/* Seção: Justificativa */}
                <Row>
                    <FormTextArea
                        label="Justificativa"
                        name="justificativa"
                        rows={4}
                        placeholder="Informe a justificativa"
                        span={12}
                    />
                </Row>

                {/* Tabela de documentos */}
                <Table
                    dataSource={documentos}
                    columns={columns()}
                    rowKey="id"
                    pagination={false}
                />
            </MasterDetailForm>
        </Modal>
    );
};

export default ModalDocumentacaoRequisitante;
